import IconButton from '@/global/components/Button/IconButton'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import { OrigensEnum, parserRegisterOrigin } from '@acme/common'
import { StatusAprovacao } from '@acme/core'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import dayjs from 'dayjs'
import { CleaveTextField } from 'global/components/Form/CleaveTextField'
import { useAuth } from 'hooks'
import {
    CircleArrowLeftIcon,
    CircleArrowRightIcon,
    HourglassIcon,
    LockKeyholeIcon,
    OctagonAlertIcon,
    PencilIcon,
    TriangleAlertIcon,
    XIcon,
} from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { registros } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import ModalJustificativaRegistro from './modals/JustificativaRegistro'
import { useDigitacaoContext } from './pages/useDigitacaoContext'
import { useUpdateDigitacaoQueryData } from './pages/useDigitacaoData'
import { entradasSaidas } from './utils/helpers'

type InputProps = {
    dia: string
    funcionarioId: number
    entSai: string
    defaultValue?: string
    status?: keyof typeof StatusAprovacao
    origem?: number
    registroId?: number
    disabled?: boolean
    saidaDiaAnterior?: boolean
    entradaDiaSeguinte?: boolean
    diaRegistro?: dayjs.Dayjs
}

export default function Input({
    dia,
    entSai,
    defaultValue = '',
    status,
    origem,
    funcionarioId,
    registroId,
    disabled,
    entradaDiaSeguinte,
    saidaDiaAnterior,
    diaRegistro,
}: InputProps) {
    const {
        decoded: { categoria },
    } = useAuth()
    const { view } = useDigitacaoContext()
    const navigate = useNavigate()
    const empresaId = useStore(state => state.companyId)
    const justificativaPadrao = useStore(
        state => state.company?.justificativaPadraoRegistro,
    )
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const { mutateAsync, isLoading, isError, error, reset } =
        registros.mutations.useCreate({
            queryParams: {
                throwOnDuplicate: true,
            },
            onSuccess: payload => {
                setQueryData('REGISTRO', payload)
                setExigirJustificativa(false)
                setTimeout(() => {
                    focusNextElement()
                }, 100)
            },
            onError: e => {
                toast.error(e.message)
            },
        })
    const [value, setValue] = React.useState(defaultValue)
    const [isHover, setIsHover] = React.useState(false)
    const [exigirJustificativa, setExigirJustificativa] = React.useState(false)
    const { color, title } = parserRegisterOrigin(origem!)

    const focusNextElement = () => {
        // Encontrar o índice atual do entSai no array entradasSaidas
        const currentIndex = entradasSaidas.indexOf(entSai as any)

        if (currentIndex === -1) return

        // Tentar focar o próximo elemento na mesma linha primeiro
        for (let i = currentIndex + 1; i < entradasSaidas.length; i++) {
            const nextElementId =
                view === 'mensal'
                    ? `Registros_${dia}_${entradasSaidas[i]}`
                    : `Registros_${dia}_${entradasSaidas[i]}_${funcionarioId}`
            const nextElement = document.getElementById(nextElementId)

            if (nextElement && !nextElement.hasAttribute('disabled')) {
                nextElement.focus()
                return
            }
        }

        // Se não encontrou na mesma linha, tentar o primeiro elemento da próxima linha (próximo dia)
        // Isso só se aplica se estivermos na visualização mensal
        if (view === 'mensal') {
            const registrosContainer = document.getElementById(
                `Registros_${dia}`,
            )
            if (registrosContainer) {
                // Encontrar o próximo container de registros
                const currentRow = registrosContainer.closest(
                    '[data-index]',
                ) as HTMLElement
                if (currentRow) {
                    const currentRowIndex = parseInt(
                        currentRow.dataset.index || '0',
                    )
                    const nextRow = document.querySelector(
                        `[data-index="${currentRowIndex + 1}"]`,
                    )

                    if (nextRow) {
                        const firstInput = nextRow.querySelector(
                            'input:not([disabled])',
                        ) as HTMLInputElement
                        if (firstInput) {
                            firstInput.focus()
                            return
                        }
                    }
                }
            }
        }
    }

    const handleBlur = () => {
        if (isError || disabled || defaultValue) return
        if (value.length < 1) return
        if (!justificativaPadrao) setExigirJustificativa(true)
        else criarRegistro(justificativaPadrao)
    }

    async function criarRegistro(justificativa: string) {
        const hora = value.length === 1 ? `0${value}:00` : value.padEnd(5, '0')
        return mutateAsync({
            empresaId,
            funcionarioId,
            data: dia,
            origem: [
                CategoriaEnum.Cliente,
                CategoriaEnum.Supervisor,
                CategoriaEnum.Avaliacao,
            ].includes(categoria)
                ? OrigensEnum.MANUAL_SUPERVISOR
                : OrigensEnum.MANUAL_FUNCIONARIO,
            hora,
            justificativa,
        } as any)
    }

    const Icone = useMemo(() => {
        if (isLoading) return <CircularProgress size={14} />

        if (status) {
            if (saidaDiaAnterior) {
                return (
                    <div
                        title={`${title} (Saída do dia anterior)`}
                        className="flex items-center p-0"
                    >
                        <CircleArrowLeftIcon
                            style={{
                                color,
                            }}
                            className="outlined size-5"
                        />
                    </div>
                )
            } else if (entradaDiaSeguinte) {
                return (
                    <div
                        title={`${title} (Entrada do dia seguinte)`}
                        className="flex items-center p-0"
                    >
                        <CircleArrowRightIcon
                            style={{
                                color,
                            }}
                            className="outlined size-5"
                        />
                    </div>
                )
            } else {
                switch (status) {
                    case StatusAprovacao.APROVADO:
                        return (
                            <div
                                title={title}
                                className="size-4 rounded-full"
                                style={{
                                    background: color,
                                }}
                            />
                        )
                    case StatusAprovacao.PENDENTE:
                    case StatusAprovacao.PENDENTE_EXCLUSAO:
                        return (
                            <div
                                className="flex items-center justify-center"
                                title={`${title} (Aguardando aprovação)`}
                            >
                                <HourglassIcon className="size-4 text-orange-500" />
                            </div>
                        )
                    case StatusAprovacao.REPROVADO:
                        return (
                            <div
                                className="flex items-center justify-center"
                                title={`${title} (Reprovado)`}
                            >
                                <OctagonAlertIcon className="size-4 text-red-500" />
                            </div>
                        )
                    default:
                        break
                }
            }
        }

        if (isError) {
            return (
                <Tooltip
                    title={
                        (error?.message || 'Erro ao salvar registro') +
                        ' - Clique para limpar'
                    }
                >
                    <IconButton
                        size="small"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        onClick={() => {
                            setValue('')
                            setIsHover(false)
                        }}
                        sx={{
                            width: 14,
                            p: 0,
                        }}
                        tabIndex={-1}
                    >
                        {isHover ? (
                            <XIcon className="size-4 text-red-500 stroke-3" />
                        ) : (
                            <TriangleAlertIcon className="size-4 text-red-500 stroke-3" />
                        )}
                    </IconButton>
                </Tooltip>
            )
        }

        if (!defaultValue) {
            return disabled ? (
                <div
                    title="Edição bloqueada"
                    className="flex items-center justify-center"
                >
                    <LockKeyholeIcon className="size-4 opacity-60" />
                </div>
            ) : (
                <PencilIcon className="size-4 opacity-40" />
            )
        }

        return null
    }, [
        defaultValue,
        isLoading,
        disabled,
        status,
        isError,
        isHover,
        saidaDiaAnterior,
        entradaDiaSeguinte,
    ])

    useEffect(() => {
        if (isError)
            document.getElementById(`Registros_${dia}_${entSai}`)?.focus()
    }, [isError])

    useEffect(() => {
        if (!value) reset()
    }, [value])

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    const isDiaDeslocado = diaRegistro && !diaRegistro.isSame(dia, 'day')

    const MemoizedModal = useMemo(() => {
        if (!exigirJustificativa) return null
        return (
            <ModalJustificativaRegistro
                key={`justificativa_${dia}_${entSai}`}
                onClose={() => {
                    setExigirJustificativa(false)
                    setValue('')
                    setTimeout(() => {
                        document
                            .getElementById(`Registros_${dia}_${entSai}`)
                            ?.focus()
                    }, 100)
                }}
                onSubmit={async justificativa => criarRegistro(justificativa)}
                isLoading={isLoading}
            />
        )
    }, [exigirJustificativa, isLoading])

    const key = useMemo(() => {
        if (view === 'mensal') {
            return `Registros_${dia}_${entSai}`
        } else {
            return `Registros_${dia}_${entSai}_${funcionarioId}`
        }
    }, [dia, entSai, funcionarioId, view])

    return [
        <OutlinedInput
            id={key}
            key={key}
            name={entSai}
            size="small"
            value={value}
            onChange={event => setValue(event.target.value)}
            inputComponent={CleaveTextField as any}
            onClick={
                defaultValue && registroId
                    ? () => navigate(`registros/${registroId}`)
                    : undefined
            }
            onBlur={handleBlur}
            error={isError}
            placeholder="--:--"
            className="relative w-25 min-w-23 cursor-default"
            autoComplete="off"
            inputProps={{
                options: {
                    time: true,
                    timePattern: ['h', 'm'],
                },
                style: {
                    textDecoration:
                        status === StatusAprovacao.REPROVADO
                            ? 'line-through'
                            : 'none',
                    cursor: defaultValue
                        ? 'pointer'
                        : disabled
                          ? 'not-allowed'
                          : 'text',
                },
            }}
            componentsProps={{
                input: {
                    readOnly: !!defaultValue || disabled,
                },
            }}
            endAdornment={[
                <InputAdornment
                    position="end"
                    className={cn({ 'cursor-pointer': !!defaultValue })}
                    key="icone"
                >
                    {Icone}
                </InputAdornment>,
                isDiaDeslocado && (
                    <div
                        title={`Registro deslocado do dia ${diaRegistro?.format(
                            'DD/MM',
                        )}`}
                        className="cursor-help"
                    >
                        <svg
                            key="dia-deslocado"
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute bottom-0 right-0 rounded-br-xs overflow-hidden size-5 border-r border-b"
                        >
                            <path d="M0 10L10 10L10 0" fill={color} />
                        </svg>
                    </div>
                ),
            ]}
        />,
        MemoizedModal,
    ]
}
